import {
  ConfirmModal,
  Loading,
  notify,
  useConfirmModal,
} from '@affine/component';
import { useGuard } from '@affine/core/components/guard';
import {
  AuthService,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { DocService } from '@affine/core/modules/doc';
import {
  FolderApiService,
  type GroupData,
} from '@affine/core/modules/folder-api';
import {
  DocGrantedUsersService,
  type GrantedUser,
  ObjectPermissionMutationService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { PermissionRoleMenu } from '@affine/core/modules/permissions/components/permission-role-menu';
import {
  endObjectConfigurationTrace,
  recordObjectConfigurationTraceEvent,
  traceObjectConfigurationRequest,
} from '@affine/core/modules/permissions/utils/object-configuration-trace';
import {
  createObjectPermissionRoleOptions,
  createWorkspaceCappedDocRoleOptions,
  getObjectPermissionAreaBlockedNotice,
  getWorkspaceRoleCeilingNotice,
  isDocGroupRolePartiallyLimited,
  objectPermissionRoleLabel,
  useObjectPermissionOnlineOnlyState,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import {
  resolveGrantedUsersPaginationAction,
  resolvePermissionPeoplePresentation,
} from '@affine/core/modules/permissions/utils/permission-people-presentation';
import {
  PermissionExpandableSection,
  usePermissionInviteCopy,
} from '@affine/core/modules/permissions/views/permission-create-modal-core';
import {
  PermissionGroupInviteSearch,
  PermissionGroupRow,
  PermissionMemberInviteSearch,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DocRole,
  type GetDocGroupRolesQuery,
  getDocGroupRolesQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { onBackendAvailable } from '../../../../utils/backend-availability';
import * as styles from './doc-permission-sections.css';

type DocGroupRole = GetDocGroupRolesQuery['docGroupRoles'][number];
type WorkspaceGroup = GroupData;

const DOC_ASSIGNABLE_ROLES = [
  DocRole.Manager,
  DocRole.Editor,
  DocRole.Commenter,
  DocRole.Reader,
] as const;

interface PermissionData {
  groups: WorkspaceGroup[];
  groupRoles: DocGroupRole[];
}

const EMPTY_DATA: PermissionData = { groups: [], groupRoles: [] };

const DOC_GROUP_ROLES_CACHE_LIMIT = 64;
const docGroupRolesCache = new Map<string, PermissionData>();
const docGroupRolesRequests = new Map<string, Promise<DocGroupRole[]>>();

function cacheDocPermissionGroups(
  cacheKey: string,
  groupRoles: DocGroupRole[],
  groups: WorkspaceGroup[]
): void {
  docGroupRolesCache.delete(cacheKey);
  docGroupRolesCache.set(cacheKey, { groupRoles, groups });
  while (docGroupRolesCache.size > DOC_GROUP_ROLES_CACHE_LIMIT) {
    const oldestKey = docGroupRolesCache.keys().next().value;
    if (!oldestKey) break;
    docGroupRolesCache.delete(oldestKey);
  }
}

export const DocPermissionSections = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const t = useI18n();
  const authService = useService(AuthService);
  const docService = useService(DocService);
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const folderApiService = useService(FolderApiService);
  const grantedUsersService = useService(DocGrantedUsersService);
  const mutationService = useService(ObjectPermissionMutationService);
  const workspacePermissionService = useService(WorkspacePermissionService);
  const canManage = useGuard('Doc_Users_Manage', docService.doc.id);
  const canTransferOwner = useGuard('Doc_TransferOwner', docService.doc.id);
  const isWorkspaceOwner = useLiveData(
    workspacePermissionService.permission.isOwner$
  );
  const { openConfirmModal } = useConfirmModal();
  const onlineOnly = useObjectPermissionOnlineOnlyState({
    backendUnavailable: !serverService.server,
  });
  const peopleCopy = usePermissionInviteCopy('share', 'people');
  const groupsCopy = usePermissionInviteCopy('share', 'groups');
  const roleOptions = useMemo(
    () => createObjectPermissionRoleOptions(t, DOC_ASSIGNABLE_ROLES),
    [t]
  );
  const grantedUsers = useLiveData(grantedUsersService.grantedUsers$);
  const grantedUsersLoading = useLiveData(grantedUsersService.isLoading$);
  const grantedUsersHasMore = useLiveData(grantedUsersService.hasMore$);
  const grantedUsersError = useLiveData(grantedUsersService.error$);
  const accountId = useLiveData(authService.session.account$)?.id;
  const [data, setData] = useState<PermissionData>(EMPTY_DATA);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [usersReady, setUsersReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);
  const [removeGroupId, setRemoveGroupId] = useState<string | null>(null);
  const docId = docService.doc.id;
  const workspaceId = workspaceService.workspace.id;
  const cacheKey = `${accountId ?? 'anonymous'}:${workspaceId}:${docId}`;
  const mutationsDisabled = disabled || onlineOnly.disabled;
  const usersRequestStartCountRef = useRef<number | null>(null);
  const groupsRequestPromiseRef = useRef<Promise<void> | null>(null);
  const groupsLoadGenerationRef = useRef(0);
  const loadedGroupsCacheKeyRef = useRef<string | null>(null);
  const groupsRequestSeqRef = useRef(0);

  const loadGroups = useCallback(
    async (force = false) => {
      while (groupsRequestPromiseRef.current) {
        await groupsRequestPromiseRef.current;
        if (!force) return;
      }
      if (!force && loadedGroupsCacheKeyRef.current === cacheKey) return;
      const server = serverService.server;
      if (!server) {
        setGroupsLoading(false);
        return;
      }
      const requestSeq = ++groupsRequestSeqRef.current;
      const loadGeneration = groupsLoadGenerationRef.current;
      const cachedData = docGroupRolesCache.get(cacheKey);
      if (cachedData) {
        setData(cachedData);
        setGroupsLoading(false);
        recordObjectConfigurationTraceEvent('cache-read', {
          cache: 'doc-permission-groups',
          hit: true,
        });
      } else {
        setGroupsLoading(true);
        recordObjectConfigurationTraceEvent('cache-read', {
          cache: 'doc-permission-groups',
          hit: false,
        });
      }
      setError(null);
      const request = (async () => {
        try {
          let groupRolesRequest = docGroupRolesRequests.get(cacheKey);
          if (!groupRolesRequest) {
            groupRolesRequest = traceObjectConfigurationRequest(
              'doc-group-roles',
              async () => {
                const result = await server.gql({
                  query: getDocGroupRolesQuery,
                  variables: { workspaceId, docId },
                });
                return result.docGroupRoles ?? [];
              }
            );
            docGroupRolesRequests.set(cacheKey, groupRolesRequest);
            void groupRolesRequest.then(
              () => docGroupRolesRequests.delete(cacheKey),
              () => docGroupRolesRequests.delete(cacheKey)
            );
          }
          const [groupRoles, groups] = await Promise.all([
            groupRolesRequest,
            traceObjectConfigurationRequest('workspace-groups', () =>
              folderApiService.getGroups(workspaceId)
            ),
          ]);
          if (groupsLoadGenerationRef.current !== loadGeneration) return;
          cacheDocPermissionGroups(cacheKey, groupRoles, groups);
          if (groupsRequestSeqRef.current !== requestSeq) return;
          loadedGroupsCacheKeyRef.current = cacheKey;
          setData({
            groupRoles,
            groups,
          });
          recordObjectConfigurationTraceEvent('permission-data-loaded', {
            groupRoles: groupRoles.length,
            groups: groups.length,
            users: grantedUsersService.grantedUsers$.value.length,
          });
        } catch (loadError) {
          if (groupsLoadGenerationRef.current !== loadGeneration) return;
          console.error(
            '[DocPermissionSections] Failed to load permissions',
            loadError
          );
          if (!cachedData) {
            setError(
              t['com.streamlain.share-menu.doc-permissions.load-error']()
            );
          } else {
            recordObjectConfigurationTraceEvent('cache-revalidation-failed', {
              cache: 'doc-permission-groups',
            });
          }
        } finally {
          if (
            groupsLoadGenerationRef.current === loadGeneration &&
            groupsRequestSeqRef.current === requestSeq
          ) {
            setGroupsLoading(false);
          }
        }
      })();
      groupsRequestPromiseRef.current = request;
      try {
        await request;
      } finally {
        if (groupsRequestPromiseRef.current === request) {
          groupsRequestPromiseRef.current = null;
        }
      }
    },
    [
      cacheKey,
      docId,
      folderApiService,
      grantedUsersService,
      serverService.server,
      t,
      workspaceId,
    ]
  );

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (!serverService.server) {
      setUsersReady(true);
      return;
    }
    const requestStartCount = usersRequestStartCountRef.current;
    const action = resolveGrantedUsersPaginationAction({
      error: Boolean(grantedUsersError),
      hasMore: grantedUsersHasMore,
      isLoading: grantedUsersLoading,
      loadedCount: grantedUsers.length,
      requestStartCount,
    });
    if (action === 'ready') {
      usersRequestStartCountRef.current = null;
      setUsersReady(true);
      return;
    }
    if (action === 'load-more') {
      usersRequestStartCountRef.current = grantedUsers.length;
      grantedUsersService.loadMore();
    }
  }, [
    grantedUsersError,
    grantedUsers.length,
    grantedUsersHasMore,
    grantedUsersLoading,
    grantedUsersService,
    serverService.server,
  ]);

  useEffect(
    () =>
      onBackendAvailable(() => {
        if (grantedUsersService.error$.value) {
          setUsersReady(false);
          grantedUsersService.reset();
          usersRequestStartCountRef.current = 0;
          grantedUsersService.loadMore();
        }
        docGroupRolesCache.delete(cacheKey);
        groupsLoadGenerationRef.current += 1;
        loadedGroupsCacheKeyRef.current = null;
        void loadGroups(true);
      }),
    [cacheKey, grantedUsersService, loadGroups]
  );

  useEffect(() => {
    if (groupsLoading || !usersReady || error || grantedUsersError) return;
    const frame = requestAnimationFrame(() => {
      recordObjectConfigurationTraceEvent('data-interactive');
      endObjectConfigurationTrace('doc', 'interactive-complete');
    });
    return () => cancelAnimationFrame(frame);
  }, [error, grantedUsersError, groupsLoading, usersReady]);

  const mutate = useCallback(
    async (
      action: 'grant' | 'revoke' | 'update',
      principal: { id: string; kind: 'user' | 'group' },
      role?: DocRole
    ) => {
      try {
        await mutationService.execute({
          action,
          principal,
          role,
          target: { id: docId, kind: 'doc' },
        });
        docGroupRolesCache.delete(cacheKey);
        groupsLoadGenerationRef.current += 1;
        loadedGroupsCacheKeyRef.current = null;
        grantedUsersService.reset();
        usersRequestStartCountRef.current = null;
        setUsersReady(false);
        await loadGroups(true);
      } catch (mutationError) {
        console.error(
          '[DocPermissionSections] Permission mutation failed',
          mutationError
        );
        notify.error({
          title:
            t['com.streamlain.share-menu.doc-permissions.mutation-failed'](),
        });
        return false;
      }
      return true;
    },
    [cacheKey, docId, grantedUsersService, loadGroups, mutationService, t]
  );

  const existingUserIds = useMemo(
    () => grantedUsers.map(user => user.user.id),
    [grantedUsers]
  );
  const assignedGroupIds = useMemo(
    () => new Set(data.groupRoles.map(groupRole => groupRole.groupId)),
    [data.groupRoles]
  );
  const groupNames = useMemo(
    () => new Map(data.groups.map(group => [group.id, group.name])),
    [data.groups]
  );
  const peoplePresentation = useMemo(() => {
    const query = peopleSearch.trim().toLocaleLowerCase();
    return resolvePermissionPeoplePresentation({
      owner: grantedUsers.find(user => user.role === DocRole.Owner) ?? null,
      users: grantedUsers,
      getOwnerId: owner => owner.user.id,
      getUserId: user => user.user.id,
      isOwnerUser: user => user.role === DocRole.Owner,
      isUserVisible: user =>
        !query ||
        [user.user.name, user.user.email].some(value =>
          value.toLocaleLowerCase().includes(query)
        ),
    });
  }, [grantedUsers, peopleSearch]);
  const orderedVisibleUsers = useMemo(
    () => [
      ...(peoplePresentation.owner ? [peoplePresentation.owner] : []),
      ...peoplePresentation.visibleNonOwners,
    ],
    [peoplePresentation]
  );
  const visibleGroupRoles = useMemo(() => {
    const query = groupSearch.trim().toLocaleLowerCase();
    if (!query) return data.groupRoles;
    return data.groupRoles.filter(groupRole =>
      (groupNames.get(groupRole.groupId) ?? groupRole.groupId)
        .toLocaleLowerCase()
        .includes(query)
    );
  }, [data.groupRoles, groupNames, groupSearch]);

  const userToRemove = grantedUsers.find(user => user.user.id === removeUserId);
  const groupToRemove = data.groupRoles.find(
    group => group.groupId === removeGroupId
  );

  const selectUserRole = useCallback(
    (user: GrantedUser, role: DocRole) => {
      if (role !== DocRole.Owner) {
        void mutate('update', { id: user.user.id, kind: 'user' }, role);
        return;
      }
      openConfirmModal({
        title:
          t[
            'com.streamlain.share-menu.member-management.set-as-owner.confirm.title'
          ](),
        description:
          t[
            'com.streamlain.share-menu.member-management.set-as-owner.confirm.description'
          ](),
        confirmText: t['Confirm'](),
        cancelText: t['Cancel'](),
        onConfirm: () =>
          mutate('update', { id: user.user.id, kind: 'user' }, DocRole.Owner),
      });
    },
    [mutate, openConfirmModal, t]
  );

  if (groupsLoading || !usersReady) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }
  if (error || grantedUsersError) {
    return (
      <div className={styles.error}>
        {error ?? t['com.streamlain.share-menu.doc-permissions.load-error']()}
      </div>
    );
  }

  return (
    <>
      <PermissionExpandableSection
        title={t['com.streamlain.folder-share.people']()}
      >
        {canManage ? (
          <PermissionMemberInviteSearch
            inputTestId="doc-permission-member-search"
            title={peopleCopy.title}
            placeholder={peopleCopy.placeholder}
            noResultsText={peopleCopy.noResultsText}
            offlineMessage={peopleCopy.offlineMessage}
            roleOptions={roleOptions}
            defaultRole={DocRole.Editor}
            existingUserIds={existingUserIds}
            disabled={mutationsDisabled}
            searchText={peopleSearch}
            onSearchTextChange={setPeopleSearch}
            onSelectMember={async (member, role) => {
              await mutate(
                'grant',
                { id: member.id, kind: 'user' },
                role ?? DocRole.Editor
              );
            }}
          />
        ) : null}
        <div className={styles.accessList}>
          {orderedVisibleUsers.map((user, index) => {
            const owner = user.role === DocRole.Owner;
            const notice = getObjectPermissionAreaBlockedNotice(t, user);
            const workspaceCeilingNotice = getWorkspaceRoleCeilingNotice(
              t,
              user.workspaceRole,
              [...DOC_ASSIGNABLE_ROLES, DocRole.Owner]
            );
            const expiresText =
              user.isGuest && user.guestExpiresAt
                ? t[
                    'com.streamlain.share-menu.member-management.guest-expires'
                  ]({ date: new Date(user.guestExpiresAt).toLocaleString() })
                : undefined;
            return (
              <div key={user.user.id}>
                {peoplePresentation.showOwnerDivider && index === 1 ? (
                  <div
                    className={styles.accessDivider}
                    role="separator"
                    data-testid="doc-permission-owner-divider"
                  />
                ) : null}
                <div
                  className={`${styles.accessRowFrame} ${
                    user.isAreaBlocked ? styles.areaBlockedAccessRow : ''
                  }`}
                  data-area-blocked={user.isAreaBlocked ? 'true' : undefined}
                >
                  <PermissionMemberRow
                    user={user.user}
                    dataTestId={`share-menu-member-item-${user.user.id}`}
                    subtitle={expiresText ?? user.user.email}
                    badges={
                      user.isGuest ? (
                        <span className={styles.guestBadge}>
                          {t[
                            'com.streamlain.share-menu.member-management.guest-badge'
                          ]()}
                        </span>
                      ) : undefined
                    }
                    roleSelector={
                      owner || !canManage ? (
                        <span className={styles.roleLabel}>
                          {objectPermissionRoleLabel(t, user.role)}
                        </span>
                      ) : (
                        <PermissionRoleMenu
                          role={user.role}
                          testId={`share-menu-member-role-trigger-${user.user.id}`}
                          options={createWorkspaceCappedDocRoleOptions(
                            t,
                            [...DOC_ASSIGNABLE_ROLES, DocRole.Owner],
                            user.workspaceRole
                          ).map(option =>
                            option.role === DocRole.Owner
                              ? {
                                  ...option,
                                  disabled:
                                    option.disabled ||
                                    !canTransferOwner ||
                                    !isWorkspaceOwner,
                                }
                              : option
                          )}
                          onSelect={role => selectUserRole(user, role)}
                          disabled={mutationsDisabled}
                        />
                      )
                    }
                    onRemove={
                      canManage && !owner
                        ? () => setRemoveUserId(user.user.id)
                        : undefined
                    }
                    removeDisabled={mutationsDisabled}
                  />
                  {notice ? (
                    <div className={styles.areaBlockedNotice}>{notice}</div>
                  ) : null}
                  {canManage && workspaceCeilingNotice ? (
                    <div className={styles.areaBlockedNotice}>
                      {workspaceCeilingNotice}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
          {grantedUsers.length === 0 && !peopleSearch ? (
            <div className={styles.emptyState}>
              {t['com.streamlain.share-menu.doc-permissions.no-users']()}
            </div>
          ) : null}
        </div>
      </PermissionExpandableSection>

      <PermissionExpandableSection
        title={t['com.streamlain.folder-share.groups']()}
      >
        {canManage ? (
          <PermissionGroupInviteSearch
            title={groupsCopy.title}
            placeholder={groupsCopy.placeholder}
            noResultsText={groupsCopy.noResultsText}
            offlineMessage={groupsCopy.offlineMessage}
            showOfflineMessage
            roleOptions={roleOptions}
            defaultRole={DocRole.Editor}
            groups={data.groups}
            assignedGroupIds={assignedGroupIds}
            disabled={mutationsDisabled}
            searchText={groupSearch}
            onSearchTextChange={setGroupSearch}
            getGroupDescription={(group, role) =>
              isDocGroupRolePartiallyLimited(
                role ?? DocRole.Editor,
                group.memberWorkspaceRoles ?? []
              )
                ? t['com.streamlain.permissions.group-role-partially-limited']()
                : group.description
            }
            onSelectGroup={(group, role) =>
              mutate(
                'grant',
                { id: group.id, kind: 'group' },
                role ?? DocRole.Editor
              )
            }
          />
        ) : null}
        <div className={styles.accessList}>
          {visibleGroupRoles.map(groupRole => {
            const notice = getObjectPermissionAreaBlockedNotice(t, groupRole);
            const group = data.groups.find(
              candidate => candidate.id === groupRole.groupId
            );
            const isPartiallyLimited = isDocGroupRolePartiallyLimited(
              groupRole.role,
              group?.memberWorkspaceRoles ?? []
            );
            return (
              <div
                key={groupRole.groupId}
                className={`${styles.accessRowFrame} ${
                  groupRole.isAreaBlocked ? styles.areaBlockedAccessRow : ''
                }`}
                data-area-blocked={groupRole.isAreaBlocked ? 'true' : undefined}
              >
                <PermissionGroupRow
                  groupName={
                    groupNames.get(groupRole.groupId) ?? groupRole.groupId
                  }
                  roleSlot={
                    canManage ? (
                      <PermissionRoleMenu
                        role={groupRole.role}
                        options={roleOptions}
                        onSelect={role =>
                          void mutate(
                            'update',
                            { id: groupRole.groupId, kind: 'group' },
                            role
                          )
                        }
                        disabled={mutationsDisabled}
                      />
                    ) : (
                      <span className={styles.roleLabel}>
                        {objectPermissionRoleLabel(t, groupRole.role)}
                      </span>
                    )
                  }
                  onRemove={
                    canManage
                      ? () => setRemoveGroupId(groupRole.groupId)
                      : undefined
                  }
                  removeDisabled={mutationsDisabled}
                />
                {notice ? (
                  <div className={styles.areaBlockedNotice}>{notice}</div>
                ) : null}
                {isPartiallyLimited ? (
                  <div className={styles.areaBlockedNotice}>
                    {t[
                      'com.streamlain.permissions.group-role-partially-limited'
                    ]()}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </PermissionExpandableSection>

      <ConfirmModal
        open={removeUserId !== null}
        onOpenChange={open => !open && setRemoveUserId(null)}
        title={t['com.streamlain.folder-share.remove-user-title']()}
        description={t[
          'com.streamlain.share-menu.doc-permissions.remove-user-description'
        ]({
          name: userToRemove?.user.name ?? '',
        })}
        confirmText={t['Remove']()}
        confirmButtonOptions={{ variant: 'error' }}
        onConfirm={() => {
          if (removeUserId) {
            void mutate('revoke', { id: removeUserId, kind: 'user' }).then(
              success => success && setRemoveUserId(null)
            );
          }
        }}
      />
      <ConfirmModal
        open={removeGroupId !== null}
        onOpenChange={open => !open && setRemoveGroupId(null)}
        title={t['com.streamlain.folder-share.remove-group-title']()}
        description={t[
          'com.streamlain.share-menu.doc-permissions.remove-group-description'
        ]({
          name: groupToRemove
            ? (groupNames.get(groupToRemove.groupId) ?? groupToRemove.groupId)
            : '',
        })}
        confirmText={t['Remove']()}
        confirmButtonOptions={{ variant: 'error' }}
        onConfirm={() => {
          if (removeGroupId) {
            void mutate('revoke', { id: removeGroupId, kind: 'group' }).then(
              success => success && setRemoveGroupId(null)
            );
          }
        }}
      />
    </>
  );
};
